// ============================================================
// Типы данных, соответствующие ответам API
// ============================================================

import { TranslationKey } from "./i18n/types";

export type Race = 'T' | 'Z' | 'P' | 'R';

export const RACES: readonly Race[] = ['T', 'Z', 'P', 'R'] as const;

// Тир (уровень мастерства): 1..7, где 7 = S (сильнейший). Хранится числом,
// отображается буквой. Может быть не указан (null).
export type Tier = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const TIER_LETTERS: Record<number, string> = {
    1: 'F', 2: 'E', 3: 'D', 4: 'C', 5: 'B', 6: 'A', 7: 'S',
};

/** Буква тира по среднему значению (округляем). null — если значения нет. */
export function tierLetter(value: number | null | undefined): string | null {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    const lvl = Math.max(1, Math.min(7, Math.round(value)));
    return TIER_LETTERS[lvl] ?? null;
}

export type StatKey =
    | 'adaptiveness'
    | 'greed'
    | 'survival'
    | 'turtle'
    | 'aggression'
    | 'variety';

export const STAT_KEYS: readonly StatKey[] = [
    'adaptiveness',
    'greed',
    'survival',
    'turtle',
    'aggression',
    'variety',
] as const;

//   'stat.adaptiveness': 'Адаптивность',
//   'stat.aggression': 'Агрессия',
//   'stat.turtle': 'Черепашность',
//   'stat.variety': 'Разнообразие',
//   'stat.survival': 'Выживание',
//   'stat.greed': 'Халява',

export const STAT_LANG_KEYS: readonly TranslationKey[] = [
    'stat.adaptiveness',
    'stat.greed',
    'stat.survival',
    'stat.turtle',
    'stat.aggression',
    'stat.variety',
] as const;

/** Игрок со средними значениями (ответ GET /api/players и /api/players/:id) */
export interface PlayerTilt {
    score: number;
    level: 'calm' | 'steady' | 'variable' | 'volatile' | 'tilted';
    games: number;
    decay: number;
    breaks: number;
    worst_run: number;
}

export interface ArchetypeSignal {
    style: number | null;
    behavior: number | null;
    notes: Array<{ id: string; params: Record<string, string | number> }>;
}

export interface PlayerArchetype {
    id: string;
    match: number;
    modifier: string | null;
    axes: { aggression: number; greed: number; turtle: number };
    traits: Array<{ id: string; params: Record<string, string | number> }>;
    games: number;
    votes: number;
    near?: { id: string; match: number } | null;
    signals?: {
        aggression: ArchetypeSignal;
        greed: ArchetypeSignal;
        turtle: ArchetypeSignal;
    };
    tilt?: PlayerTilt | null;
}

export interface PlayerWithStats {
    id: number;
    name: string;
    aka: string | null;
    user_id: string | null;
    username?: string | null;
    races: Race[];
    dominant_race: Race | null;
    vote_count: number;
    tier: number | null;
    ghost_tier: number | null;
    adaptiveness: number | null;
    greed: number | null;
    survival: number | null;
    turtle: number | null;
    aggression: number | null;
    variety: number | null;
    elo: number;
    games_played: number;
    wins: number;
    winrate: number;
    activity_score: number;
    avg_place: number | null;
    game_days: number;
    activity_rank: number | null;
    elo_rank: number | null;
    /** Место по MMR, смещение за прошлый игровой день и стаж на месте. */
    mmr_rank?: number | null;
    rank_prev?: number | null;
    rank_delta?: number | null;
    place_days?: number | null;
    titles?: PlayerTitle[];
    archetype?: PlayerArchetype | null;
}

/** Титул игрока (показывается в карточке справа сверху) */
export interface PlayerTitle {
    id: number;
    name: string;
    name_en?: string | null;
    color: string;
    size?: TitleSize;
}

export type TitleSize = 'small' | 'medium' | 'large' | 'xlarge';

export const TITLE_SIZES: readonly TitleSize[] = ['small', 'medium', 'large', 'xlarge'] as const;

/** Оценка в публичном списке — теперь с username */
export interface Rating {
    id: number;
    user_id: string;
    username: string;
    rater_player_id?: number | null;
    rater_player_name?: string | null;
    is_moderator: boolean;
    is_admin: boolean;
    is_ghost: boolean;
    excluded?: boolean;
    race: Race;
    tier: Tier | null;
    adaptiveness: number;
    greed: number;
    survival: number;
    turtle: number;
    aggression: number;
    variety: number;
    created_at: string;
    updated_at: string;
}

/** Оценка, которую связанный с профилем игрок поставил другому игроку */
export interface GivenRating {
    id: number;
    player_id: number;
    player_name: string | null;
    race: Race;
    tier: number | null;
    adaptiveness: number;
    greed: number;
    survival: number;
    turtle: number;
    aggression: number;
    variety: number;
    created_at: string;
    updated_at: string;
}

/** Своя оценка (ответ GET /api/players/:id/my-rating) */
export interface MyRating {
    id: number;
    race: Race;
    tier: Tier | null;
    adaptiveness: number;
    greed: number;
    survival: number;
    turtle: number;
    aggression: number;
    variety: number;
    created_at: string;
    updated_at: string;
}

/** Тело запроса на создание/обновление оценки */
export interface RatingInput {
    race: Race;
    tier: Tier | null;
    adaptiveness: number;
    greed: number;
    survival: number;
    turtle: number;
    aggression: number;
    variety: number;
}

/** Пользователь (ответ login/signup) */
export interface AuthUser {
    id: string;
    email: string;
    username?: string | null;
    is_moderator?: boolean;
    is_admin?: boolean;
    is_ghost?: boolean;
    can_rate?: boolean;
    player_id?: number | null;
    player_name?: string | null;
}

export interface LoginResponse {
    user: AuthUser;
    access_token: string;
    refresh_token: string;
    expires_at: number;
}

export interface SignupResponse {
    user: (AuthUser & { username: string }) | null;
    session: { access_token: string; refresh_token: string; expires_at: number } | null;
    note?: string;
}
// --- Ответы API ---

export interface PlayersListResponse {
    players: PlayerWithStats[];
}

export interface PlayerResponse {
    player: PlayerWithStats;
    /** Сводка реакций «отношение к игроку» (отдаёт /api/overlay/player/:id). */
    reactions?: {
        available: boolean;
        counts?: Record<string, number>;
        my?: string[];
        total?: number;
    } | null;
}

export interface RatingsListResponse {
    ratings: Rating[];
}

export interface MyRatingResponse {
    rating: MyRating | null;
}

export interface GivenRatingsResponse {
    ratings: GivenRating[];
}

export interface RatingResponse {
    rating: Rating;
}

export interface ApiError {
    error: string;
    /** Машиночитаемый код ошибки — клиент маппит его в локализованный текст. */
    code?: string;
}